import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import SiteHeader from './layout/SiteHeader';
import { hasPendingUpload } from '../utils/pendingUpload';
import { currencyHint } from '../utils/currency';
import { connectTarget } from '../utils/connectTarget';
import { isDesktop, openExternal } from '../utils/desktop';
import api from '../utils/api';

const Icon = ({ name, className = '' }) => (
  <span className={`ms-icon ${className}`}>{name}</span>
);

// Disposable-mail abuse via the email/password form pushed this to Google-only.
// Flip to true to bring the email/password form back.
const EMAIL_PASSWORD_LOGIN_ENABLED = false;

const AuthPage = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const connectNext = connectTarget(location.search);
  const { login, register, resendVerification, googleLogin, adoptSession } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Set once registration succeeds — swaps the form for a "check your email" view.
  const [registered, setRegistered] = useState(false);
  // True when a login is blocked because the email isn't verified yet.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendNotice, setResendNotice] = useState('');
  // Flips true once Google Identity Services has rendered its button, so the
  // loading fallback unmounts cleanly instead of lingering next to it.
  const [googleReady, setGoogleReady] = useState(false);
  const googleButtonRef = useRef(null);
  // True when a video picked on the landing page is waiting to upload after auth.
  const [videoWaiting, setVideoWaiting] = useState(false);

  const isLogin = mode === 'login';

  // Desktop shell: Google Identity Services refuses embedded browsers, so the
  // sign-in happens in the user's real browser and comes back through pairing.
  const desktop = isDesktop();
  const [desktopWaiting, setDesktopWaiting] = useState(false);
  const desktopPoll = useRef(null);

  const stopDesktopPoll = () => {
    if (desktopPoll.current) { clearInterval(desktopPoll.current); desktopPoll.current = null; }
    setDesktopWaiting(false);
  };

  const startDesktopGoogle = async () => {
    setError('');
    stopDesktopPoll();
    try {
      const { data } = await api.post('/api/auth/device/start');
      const site = window.location.origin;
      await openExternal(`${site}/connect?client=desktop&code=${data.userCode}`);
      setDesktopWaiting(true);
      const interval = Math.max(2, data.interval || 5) * 1000;
      const deadline = Date.now() + (data.expiresIn || 600) * 1000;
      desktopPoll.current = setInterval(async () => {
        if (Date.now() > deadline) {
          stopDesktopPoll();
          setError('Sign-in timed out. Try again.');
          return;
        }
        try {
          const res = await api.post('/api/auth/device/session', { deviceCode: data.deviceCode });
          if (res.status === 202) return; // still waiting for approval in the browser
          stopDesktopPoll();
          adoptSession(res.data);
          navigate(connectNext || '/app', { replace: true });
        } catch (err) {
          if (err.response?.status === 202) return;
          stopDesktopPoll();
          setError(err.response?.data?.error || 'Sign-in could not be completed. Try again.');
        }
      }, interval);
    } catch (err) {
      stopDesktopPoll();
      setError(err.response?.data?.error || 'Could not start sign-in. Check your connection and try again.');
    }
  };

  // The browser bounces focus back to the app on approval; the poll above is
  // what actually completes sign-in. Stop polling if the page unmounts first.
  useEffect(() => () => { if (desktopPoll.current) clearInterval(desktopPoll.current); }, []);

  useEffect(() => {
    let cancelled = false;
    hasPendingUpload().then((v) => { if (!cancelled) setVideoWaiting(v); });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendNotice('');
    setNeedsVerification(false);

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        // Account is created but not logged in — show the check-email screen.
        await register(email, password, confirmPassword);
        setRegistered(true);
        return;
      }
      const pendingPlan = localStorage.getItem('pendingPlanCheckout');
      if (pendingPlan) {
        localStorage.removeItem('pendingPlanCheckout');
        const currentToken = localStorage.getItem('auth_token');
        const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/payments/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`
          },
          body: JSON.stringify({ planId: pendingPlan, currency: currencyHint() })
        });
        const data = await res.json();
        if (res.ok && data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
      }
      navigate('/app', { replace: true });
    } catch (err) {
      if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
      }
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendNotice('');
    setLoading(true);
    try {
      const { message } = await resendVerification(email);
      setResendNotice(message || 'Verification link sent. Check your inbox.');
    } catch {
      setError('Could not resend the verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentModeRef = useRef(mode);
  useEffect(() => {
    currentModeRef.current = mode;
    // Switching between Sign In / Create Account resets transient form state so a
    // prior success/error screen doesn't bleed across the tabs.
    setRegistered(false);
    setNeedsVerification(false);
    setError('');
    setResendNotice('');
    setConfirmPassword('');
  }, [mode]);

  useEffect(() => {
    // Desktop shell: never load Google Identity Services; see startDesktopGoogle.
    if (desktop) return;
    // The gsi/client script is injected here on demand (NOT in index.html):
    // it sets dozens of third-party cookies and ships ~100 KB of JS, so only
    // the auth pages should pay for it. Injecting twice is harmless but the
    // guard keeps login <-> register navigation from stacking script tags.
    if (!window.google?.accounts?.id && !document.getElementById('gsi-client')) {
      const script = document.createElement('script');
      script.id = 'gsi-client';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      document.head.appendChild(script);
    }

    // Wait for the Google script to load
    const initializeGoogleSignIn = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
          callback: async (response) => {
            try {
              setLoading(true);
              await googleLogin(response.credential);
                      const pendingPlan = localStorage.getItem('pendingPlanCheckout');
              if (pendingPlan) {
                localStorage.removeItem('pendingPlanCheckout');
                const currentToken = localStorage.getItem('auth_token');
                const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/payments/checkout`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                  },
                  body: JSON.stringify({ planId: pendingPlan, currency: currencyHint() })
                });
                const data = await res.json();
                if (res.ok && data.checkout_url) {
                  window.location.href = data.checkout_url;
                  return;
                }
              }
              navigate('/app', { replace: true });
            } catch (err) {
              // A rejected token and an unreachable API look identical from here, and
              // "try again" is useless advice when nothing is listening on the API port.
              // err.response is absent only when the request never got a reply.
              setError(err.response?.data?.error
                || (err.response
                  ? 'Google sign-in failed. Please try again.'
                  : 'Could not reach the server. Check that it is running, then try again.'));
            } finally {
              setLoading(false);
            }
          },
          context: currentModeRef.current === 'login' ? 'signin' : 'signup',
        });

        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: 'outline',
            size: 'large',
            // Google SDK only accepts integers for width (up to 400). The ref'd
            // div is empty (width 0) until Google injects its iframe, so measure
            // the w-full parent wrapper instead to keep the iframe inside the card
            width: Math.min(400, googleButtonRef.current?.parentElement?.offsetWidth || 400),
            shape: 'rectangular',
            text: currentModeRef.current === 'login' ? 'signin_with' : 'signup_with',
            logo_alignment: 'center'
          });
          setGoogleReady(true);
          // One Tap: surface the Google account chooser automatically so a
          // returning user can sign in with a single tap instead of finding
          // the button. Google rate-limits this itself (cooldown after
          // dismissal), so it won't nag users who close it.
          window.google.accounts.id.prompt();
        }
      } else {
        setTimeout(initializeGoogleSignIn, 100); // Retry if not yet loaded
      }
    };

    initializeGoogleSignIn();
  }, [googleLogin, navigate, desktop]);

  return (
    <div className="min-h-screen w-full bg-[#FDFBF7] dark:bg-stone-950 selection:bg-stone-200 selection:text-stone-900 flex flex-col relative font-jakarta">
      <Helmet>
        <title>{isLogin ? 'Login | DesiCaptions' : 'Sign Up | DesiCaptions'}</title>
        <meta
          name="description"
          content={isLogin
            ? 'Log in to DesiCaptions to manage your projects and generate Hindi and Hinglish subtitles for your videos.'
            : 'Create a free DesiCaptions account and caption your first Hindi or Hinglish video in a minute or two. No credit card needed.'}
        />
        <link rel="canonical" href={`https://www.desicaptions.com/${isLogin ? 'login' : 'register'}`} />
      </Helmet>

      {/* Same navbar as the landing page so auth pages don't feel like a dead end */}
      <SiteHeader />

      {/* Main card */}
      <main className="flex-grow flex items-center justify-center p-6 pt-28 z-10 w-full">
        <div className="w-full max-w-[500px] my-auto">
          <div
            className="bg-white dark:bg-stone-900 border border-stone-200/60 dark:border-stone-800 rounded-3xl p-6 sm:p-10 md:px-14 md:py-12 relative overflow-hidden"
            style={{ boxShadow: '0 20px 60px -15px rgba(0,0,0,0.05)' }}
          >

            {/* Mode tabs */}
            <div className="flex bg-stone-100 dark:bg-stone-800 rounded-xl p-1 mb-8 relative z-10">
              <Link
                to="/login"
                replace
                className={`flex-1 py-2 text-sm font-bold font-jetbrains rounded-lg text-center transition-all duration-200 ${
                  isLogin
                    ? 'bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-sm'
                    : 'text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300'
                }`}
              >
                Login
              </Link>
              <Link
                to="/register"
                replace
                className={`flex-1 py-2 text-sm font-bold font-jetbrains rounded-lg text-center transition-all duration-200 ${
                  !isLogin
                    ? 'bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-sm'
                    : 'text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300'
                }`}
              >
                Sign Up
              </Link>
            </div>

            {registered ? (
              <div className="text-center relative z-10 py-4">
                <div className="w-16 h-16 bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon name="mark_email_read" className="text-3xl" />
                </div>
                <h1 className="text-[24px] font-serif tracking-tight text-[#1A1A1A] dark:text-stone-100 mb-3 font-bold">Check your email</h1>
                <p className="text-stone-500 dark:text-stone-400 font-jakarta text-sm mb-6">
                  We've sent a verification link to <span className="font-semibold text-stone-700 dark:text-stone-300">{email}</span>. Click it to activate your account, then log in.
                </p>
                {resendNotice && (
                  <div className="text-sm text-green-700 bg-green-50 border border-green-100 dark:text-green-400 dark:bg-green-950/40 dark:border-green-900 rounded-xl px-4 py-3 font-jakarta mb-4">
                    {resendNotice}
                  </div>
                )}
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 dark:text-red-400 dark:bg-red-950/40 dark:border-red-900 rounded-xl px-4 py-3 font-jakarta mb-4">
                    {error}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Sending...' : "Didn't get it? Resend link"}
                </button>
                <div className="mt-6">
                  <Link to="/login" replace className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A1A1A] text-white dark:bg-stone-100 dark:text-stone-900 rounded-xl font-mono text-sm font-semibold hover:bg-black dark:hover:bg-white transition-all">
                    <Icon name="arrow_back" className="text-base" />
                    Back to Login
                  </Link>
                </div>
              </div>
            ) : (
              <>
            {videoWaiting && (
              <div className="flex items-center gap-3 px-4 py-3 mb-6 rounded-xl bg-indigo-50 border border-indigo-100 dark:bg-indigo-950/40 dark:border-indigo-900 relative z-10">
                <Icon name="movie" className="text-xl text-indigo-600 dark:text-indigo-400 shrink-0" />
                <p className="text-sm font-jakarta text-indigo-900 dark:text-indigo-200 leading-snug">
                  Your video is ready.{' '}
                  {isLogin ? 'Sign in and captions start automatically.' : 'Create your free account and captions start automatically.'}
                </p>
              </div>
            )}
            <div className="text-center mb-8 relative z-10">
              <p className="text-stone-400 dark:text-stone-500 font-jakarta text-sm">
                {isLogin
                  ? 'Sign in with Google to access your workspace.'
                  : 'Sign up with Google to start generating subtitles.'}
              </p>
            </div>

            {/* Google-first: the preferred sign-in method, shown above the email
                form so it's visible without scrolling on mobile.
                The ref'd div is owned entirely by Google Identity Services —
                renderButton() injects its own iframe into it. React must NOT
                render any children into that node, or reconciliation will try
                to removeChild a node Google has already moved, throwing
                "Failed to execute 'removeChild' on 'Node'". So Google gets its
                own empty child div, and the loading fallback lives in a SIBLING. */}
            <div className="w-full flex justify-center relative z-10">
              {desktop ? (
                  <button type="button" onClick={startDesktopGoogle} disabled={desktopWaiting}
                    className="flex items-center justify-center gap-3 w-4/5 py-2.5 border border-stone-200 dark:border-stone-700 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors mx-auto text-sm font-medium text-stone-600 dark:text-stone-300 disabled:opacity-60">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5"/>
                    {desktopWaiting ? 'Waiting for approval in your browser…' : 'Continue with Google in your browser'}
                  </button>
              ) : (
              <div ref={googleButtonRef} />
              )}
              {/* Fallback shown until the Google script finishes loading */}
              {!desktop && !googleReady && (
                  <button className="flex items-center justify-center gap-3 w-4/5 py-2.5 border border-stone-200 dark:border-stone-700 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors mx-auto text-sm font-medium text-stone-600 dark:text-stone-300">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5"/>
                    Continue with Google
                  </button>
              )}
            </div>
            <p className="text-center text-[11px] text-stone-400 dark:text-stone-500 font-jakarta mt-2 relative z-10">
              We only support Google sign-in right now.
            </p>

            {/* Error (Google sign-in failures surface here while email/password is disabled below) */}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 dark:text-red-400 dark:bg-red-950/40 dark:border-red-900 rounded-xl px-4 py-3 font-jakarta mt-6 relative z-10">
                {error}
              </div>
            )}

            {EMAIL_PASSWORD_LOGIN_ENABLED && (
            <>
            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200 dark:border-stone-800" />
              </div>
              <div className="relative flex justify-center text-[10px] uppercase">
                <span className="bg-white dark:bg-stone-900 px-4 text-stone-400 dark:text-stone-500 font-jetbrains font-medium tracking-widest">Or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 relative z-10 mt-6">
              {/* Email */}
              <div className="space-y-2">
                <label className="block text-xs font-jetbrains font-semibold text-stone-600 dark:text-stone-300 uppercase tracking-widest" htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-[#FDFBF7] dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all duration-200 placeholder:text-stone-400 dark:placeholder:text-stone-500 text-stone-900 dark:text-stone-100 font-jakarta text-sm hover:border-stone-300 dark:hover:border-stone-600"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-jetbrains font-semibold text-stone-600 dark:text-stone-300 uppercase tracking-widest" htmlFor="password">
                    Password
                  </label>
                  {isLogin && (
                    <Link to="/forgot-password" className="text-[11px] font-semibold text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors font-jakarta">
                      Forgot Password?
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    placeholder={isLogin ? '••••••••' : 'At least 8 characters'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-[#FDFBF7] dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all duration-200 placeholder:text-stone-400 dark:placeholder:text-stone-500 text-stone-900 dark:text-stone-100 font-jakarta hover:border-stone-300 dark:hover:border-stone-600 tracking-widest text-lg pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 transition-colors p-1 flex items-center justify-center"
                    tabIndex="-1"
                  >
                    <Icon name={showPassword ? "visibility_off" : "visibility"} className="text-xl" />
                  </button>
                </div>
              </div>

              {/* Confirm Password (register only) */}
              {!isLogin && (
                <div className="space-y-2">
                  <label className="block text-xs font-jetbrains font-semibold text-stone-600 dark:text-stone-300 uppercase tracking-widest" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-[#FDFBF7] dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all duration-200 placeholder:text-stone-400 dark:placeholder:text-stone-500 text-stone-900 dark:text-stone-100 font-jakarta hover:border-stone-300 dark:hover:border-stone-600 tracking-widest text-lg"
                  />
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 dark:text-red-400 dark:bg-red-950/40 dark:border-red-900 rounded-xl px-4 py-3 font-jakarta">
                  {error}
                </div>
              )}

              {/* Resend verification (shown when a login is blocked as unverified) */}
              {needsVerification && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="w-full text-sm font-semibold text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Resend verification email'}
                </button>
              )}
              {resendNotice && (
                <div className="text-sm text-green-700 bg-green-50 border border-green-100 dark:text-green-400 dark:bg-green-950/40 dark:border-green-900 rounded-xl px-4 py-3 font-jakarta">
                  {resendNotice}
                </div>
              )}

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1A1A1A] hover:bg-black dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono text-sm font-semibold py-3.5 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {loading ? 'WAIT...' : isLogin ? 'Login' : 'Sign Up'}
                  {!loading && <Icon name="arrow_forward" className="text-[16px] ml-1" />}
                </button>
              </div>
            </form>
            </>
            )}
              </>
            )}

          </div>

          {/* Footer links */}
          <div className="mt-8 mb-6 flex justify-center gap-6 text-[11px] uppercase font-jetbrains font-medium tracking-widest text-stone-400 dark:text-stone-500">
            <Link to="/privacy" className="hover:text-stone-600 dark:hover:text-stone-300 transition-colors">Privacy Policy</Link>
            <span className="text-stone-300 dark:text-stone-700">•</span>
            <Link to="/terms" className="hover:text-stone-600 dark:hover:text-stone-300 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
